'use client';

import { useState, useRef, useEffect, FormEvent } from 'react';
import html2canvas from 'html2canvas';

interface Message {
  role: 'user' | 'assistant';
  content: string;
}

export default function Home() {
  const [messages, setMessages] = useState<Message[]>([
    { role: 'assistant', content: '您好~ 我是 Daibuild 设计顾问,请告诉我您理想的别墅风格、面积、户型,我们可以一起把它构建出来。' }
  ]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [showInquiry, setShowInquiry] = useState(false);
  const [inquiryName, setInquiryName] = useState('');
  const [inquiryPhone, setInquiryPhone] = useState('');
  const [inquiryNote, setInquiryNote] = useState('');
  const [inquirySent, setInquirySent] = useState(false);
  const [savedNotice, setSavedNotice] = useState('');
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const editorPanelRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const handleSubmit = async (e?: FormEvent) => {
    if (e) e.preventDefault();
    const text = input.trim();
    if (!text || loading) return;

    const newMessages = [...messages, { role: 'user' as const, content: text }];
    setMessages(newMessages);
    setInput('');
    setLoading(true);

    try {
      const res = await fetch('/api/chat', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ messages: newMessages }),
      });
      const data = await res.json();
      const reply = data.reply || data.error || '稍候,正在为您整理...';
      setMessages(prev => [...prev, { role: 'assistant' as const, content: reply }]);
    } catch (err) {
      const msg = err instanceof Error ? err.message : '网络错误';
      setMessages(prev => [...prev, { role: 'assistant' as const, content: '抱歉,' + msg + ',请稍后再试。' }]);
    } finally {
      setLoading(false);
    }
  };

  const handleScreenshot = async () => {
    if (!editorPanelRef.current) return;
    try {
      const canvas = await html2canvas(editorPanelRef.current, { useCORS: true, allowTaint: true });
      const link = document.createElement('a');
      link.download = 'villa-design-' + Date.now() + '.png';
      link.href = canvas.toDataURL();
      link.click();
    } catch {
      alert('截图功能需要在 HTTPS 环境下完整使用,3D 区域可能无法截取');
    }
  };

  const handleSave = () => {
    try {
      const data = { messages, savedAt: new Date().toISOString() };
      const key = 'villa-design-' + Date.now();
      localStorage.setItem(key, JSON.stringify(data));
      setSavedNotice('方案已保存到本地');
      setTimeout(() => setSavedNotice(''), 3000);
    } catch {
      setSavedNotice('保存失败');
      setTimeout(() => setSavedNotice(''), 3000);
    }
  };

  const handleInquirySubmit = async () => {
    if (!inquiryName.trim() || !inquiryPhone.trim()) {
      alert('请填写姓名和手机号');
      return;
    }
    try {
      const inquiry = {
        name: inquiryName,
        phone: inquiryPhone,
        note: inquiryNote,
        conversation: messages,
        createdAt: new Date().toISOString(),
      };
      localStorage.setItem('inquiry-' + Date.now(), JSON.stringify(inquiry));
      setInquirySent(true);
      setTimeout(() => {
        setShowInquiry(false);
        setInquirySent(false);
        setInquiryName('');
        setInquiryPhone('');
        setInquiryNote('');
      }, 2500);
    } catch {
      alert('提交失败,请稍后再试');
    }
  };

  return (
    <div className="h-screen w-screen bg-[#FAFAFA] text-[#18181B] flex flex-col overflow-hidden font-sans">
      
      <header className="flex items-center justify-between px-6 py-4 bg-white border-b border-[#E8E8E8]">
        <div className="flex items-center gap-3">
          <div className="w-7 h-7 rounded-md bg-gradient-to-br from-[#1E40AF] to-[#2563EB] flex items-center justify-center">
            <span className="text-white text-xs font-bold">D</span>
          </div>
          <div>
            <div className="text-sm font-semibold text-[#18181B] tracking-wide">Daibuild</div>
            <div className="text-[10px] text-[#A1A1AA] -mt-0.5">villa.biekuile.com</div>
          </div>
        </div>
        <div className="flex items-center gap-6 text-sm">
          <div className="flex gap-3 text-[13px] text-[#71717A]">
            <span className="text-[#18181B] font-medium cursor-pointer">中文</span>
            <span className="text-[#E8E8E8]">|</span>
            <span className="cursor-pointer hover:text-[#18181B]">EN</span>
          </div>
          <button className="text-[13px] text-[#71717A] hover:text-[#18181B]">我的方案</button>
          <button onClick={() => setShowInquiry(true)} className="text-[13px] px-4 py-1.5 bg-[#1E40AF] text-white rounded-md hover:bg-[#1E3A8A] transition-colors font-medium">
            联系我们
          </button>
        </div>
      </header>

      <div className="flex-1 flex gap-4 p-4 overflow-hidden">
        
        <div className="w-[420px] bg-white border border-[#E8E8E8] rounded-xl flex flex-col overflow-hidden shadow-sm">
          
          <div className="px-5 py-4 border-b border-[#F0F0F0] flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[#DBEAFE] to-[#BFDBFE] flex items-center justify-center flex-shrink-0">
              <svg className="w-5 h-5 text-[#1E40AF]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
              </svg>
            </div>
            <div>
              <div className="text-[14px] font-medium text-[#18181B]">Daibuild Studio</div>
              <div className="text-[11px] text-[#10B981] flex items-center gap-1.5 mt-0.5">
                <span className="w-1.5 h-1.5 rounded-full bg-[#10B981]"></span>
                在线
              </div>
            </div>
          </div>
          
          <div className="flex-1 overflow-y-auto px-5 py-4 flex flex-col gap-3">
            {messages.map((msg, i) => (
              <div key={i} className={'max-w-[85%] px-4 py-2.5 rounded-2xl text-[13px] leading-relaxed ' + (
                msg.role === 'user' 
                  ? 'self-end bg-[#1E40AF] text-white rounded-br-md' 
                  : 'self-start bg-[#F4F4F5] text-[#18181B] rounded-bl-md'
              )}>
                {msg.content}
              </div>
            ))}
            {loading && (
              <div className="self-start bg-[#F4F4F5] text-[#71717A] px-4 py-2.5 rounded-2xl rounded-bl-md text-[13px] flex gap-1">
                <span className="w-1.5 h-1.5 rounded-full bg-[#71717A] animate-bounce" style={{ animationDelay: '0ms' }}></span>
                <span className="w-1.5 h-1.5 rounded-full bg-[#71717A] animate-bounce" style={{ animationDelay: '150ms' }}></span>
                <span className="w-1.5 h-1.5 rounded-full bg-[#71717A] animate-bounce" style={{ animationDelay: '300ms' }}></span>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>
          
          <form onSubmit={handleSubmit} className="px-5 py-4 border-t border-[#F0F0F0] flex gap-2 bg-white">
            <input
              type="text"
              value={input}
              onChange={e => setInput(e.target.value)}
              disabled={loading}
              placeholder="描述您理想的别墅..."
              className="flex-1 text-[13px] px-4 py-2.5 bg-[#F9F9FA] border border-[#E8E8E8] rounded-lg text-[#18181B] placeholder:text-[#A1A1AA] outline-none focus:border-[#1E40AF] focus:bg-white transition-colors disabled:opacity-50"
            />
            <button
              type="submit"
              disabled={loading || !input.trim()}
              className="text-[13px] px-5 py-2.5 bg-[#1E40AF] text-white rounded-lg hover:bg-[#1E3A8A] disabled:opacity-30 disabled:cursor-not-allowed transition-colors font-medium"
            >
              发送
            </button>
          </form>
        </div>

        <div ref={editorPanelRef} className="flex-1 bg-white border border-[#E8E8E8] rounded-xl overflow-hidden flex flex-col shadow-sm">
          
          <div className="px-5 py-3 border-b border-[#F0F0F0] flex items-center justify-between bg-white">
            <div className="flex items-center gap-2 text-[13px] text-[#71717A]">
              <span className="text-[#18181B] font-medium">设计中</span>
              <span className="text-[#D4D4D8]">›</span>
              <span>户型选择</span>
              <span className="text-[#D4D4D8]">›</span>
              <span>询价</span>
            </div>
            <div className="flex gap-6 text-[13px]">
              <div>
                <span className="text-[#A1A1AA]">面积  </span>
                <span className="text-[#18181B] font-semibold">--</span>
                <span className="text-[#71717A] ml-0.5">㎡</span>
              </div>
              <div>
                <span className="text-[#A1A1AA]">参考造价  </span>
                <span className="text-[#059669] font-semibold">¥--</span>
              </div>
            </div>
          </div>
          
          <iframe
            src="https://editor.biekuile.com"
            className="flex-1 w-full border-0 bg-[#F4F4F5]"
            title="3D 编辑器"
          />
          
          <div className="px-5 py-3 border-t border-[#F0F0F0] flex items-center justify-between bg-white">
            <div className="text-[12px] text-[#71717A]">
              {savedNotice && <span className="text-[#059669]">{savedNotice}</span>}
            </div>
            <div className="flex gap-2">
              <button
                onClick={handleScreenshot}
                className="text-[13px] px-4 py-2 bg-white text-[#52525B] border border-[#E8E8E8] rounded-md hover:bg-[#F9F9FA] hover:border-[#D4D4D8] transition-colors flex items-center gap-1.5"
              >
                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
                截图
              </button>
              <button
                onClick={handleSave}
                className="text-[13px] px-4 py-2 bg-white text-[#52525B] border border-[#E8E8E8] rounded-md hover:bg-[#F9F9FA] hover:border-[#D4D4D8] transition-colors flex items-center gap-1.5"
              >
                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
                保存方案
              </button>
              <button
                onClick={() => setShowInquiry(true)}
                className="text-[13px] px-5 py-2 bg-[#1E40AF] text-white rounded-md hover:bg-[#1E3A8A] transition-colors font-medium flex items-center gap-1.5"
              >
                立即询价
                <span>→</span>
              </button>
            </div>
          </div>
        </div>

      </div>

      {showInquiry && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={() => !inquirySent && setShowInquiry(false)}>
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-md p-6" onClick={e => e.stopPropagation()}>
            {!inquirySent ? (
              <>
                <div className="mb-5">
                  <h2 className="text-[18px] font-semibold text-[#18181B] mb-1">立即询价</h2>
                  <p className="text-[13px] text-[#71717A]">留下您的联系方式,我们的设计顾问将在24小时内联系您。</p>
                </div>
                <div className="space-y-3">
                  <div>
                    <label className="text-[12px] text-[#52525B] block mb-1.5">姓名 <span className="text-[#DC2626]">*</span></label>
                    <input
                      type="text"
                      value={inquiryName}
                      onChange={e => setInquiryName(e.target.value)}
                      className="w-full text-[13px] px-3 py-2.5 bg-[#F9F9FA] border border-[#E8E8E8] rounded-lg outline-none focus:border-[#1E40AF] focus:bg-white"
                      placeholder="您的称呼"
                    />
                  </div>
                  <div>
                    <label className="text-[12px] text-[#52525B] block mb-1.5">手机号 <span className="text-[#DC2626]">*</span></label>
                    <input
                      type="tel"
                      value={inquiryPhone}
                      onChange={e => setInquiryPhone(e.target.value)}
                      className="w-full text-[13px] px-3 py-2.5 bg-[#F9F9FA] border border-[#E8E8E8] rounded-lg outline-none focus:border-[#1E40AF] focus:bg-white"
                      placeholder="11位手机号"
                    />
                  </div>
                  <div>
                    <label className="text-[12px] text-[#52525B] block mb-1.5">需求备注</label>
                    <textarea
                      value={inquiryNote}
                      onChange={e => setInquiryNote(e.target.value)}
                      rows={3}
                      className="w-full text-[13px] px-3 py-2.5 bg-[#F9F9FA] border border-[#E8E8E8] rounded-lg outline-none focus:border-[#1E40AF] focus:bg-white resize-none"
                      placeholder="例如:预算范围、地点、希望开工时间..."
                    />
                  </div>
                </div>
                <div className="flex gap-2 mt-5">
                  <button
                    onClick={() => setShowInquiry(false)}
                    className="flex-1 text-[13px] px-4 py-2.5 bg-white text-[#52525B] border border-[#E8E8E8] rounded-lg hover:bg-[#F9F9FA]"
                  >
                    取消
                  </button>
                  <button
                    onClick={handleInquirySubmit}
                    className="flex-1 text-[13px] px-4 py-2.5 bg-[#1E40AF] text-white rounded-lg hover:bg-[#1E3A8A] font-medium"
                  >
                    提交
                  </button>
                </div>
                <p className="text-[11px] text-[#A1A1AA] text-center mt-4">您的对话内容将一并提交以便顾问了解需求</p>
              </>
            ) : (
              <div className="text-center py-6">
                <div className="w-14 h-14 rounded-full bg-[#D1FAE5] flex items-center justify-center mx-auto mb-4">
                  <svg className="w-7 h-7 text-[#059669]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                </div>
                <h3 className="text-[16px] font-semibold text-[#18181B] mb-1">提交成功</h3>
                <p className="text-[13px] text-[#71717A]">我们的设计顾问将尽快与您联系</p>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
